import { Component, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators, FormArray, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { NgFor, NgIf } from '@angular/common';

@Component({
  selector: 'app-new-cointype-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    ReactiveFormsModule,
    NgFor,
    NgIf
  ],
  templateUrl: './new-cointype-dialog.component.html',
  styleUrls: ['./new-cointype-dialog.component.css']
})
export class NewCointypeDialogComponent {
  addCoinTypeForm: FormGroup;
  isEdit: boolean = false;
  dialogData: any;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<NewCointypeDialogComponent>
  ) {
    this.dialogData = inject(MAT_DIALOG_DATA);
    this.isEdit = !!(this.dialogData && this.dialogData.coinType);
    this.addCoinTypeForm = this.fb.group({
      value: [null, Validators.required],
      baseAmount: [null, Validators.required],
      tiers: this.fb.array([])
    });

    if (this.isEdit) {
      // Editing: patch value and tiers
      this.addCoinTypeForm.patchValue({ 
        value: this.dialogData.coinType.value,
        baseAmount: this.dialogData.coinType.baseAmount
      });
      const tiersArray = this.addCoinTypeForm.get('tiers') as FormArray;
      this.dialogData.coinType.tiers.forEach((tier: any) => {
        tiersArray.push(this.fb.group({
          breakPoint: [tier.breakPoint, Validators.required],
          bonusAmount: [tier.bonusAmount, Validators.required],
          coinGain: [tier.coinGain, Validators.required]
        }));
      });
    } else {
      // Creating: add one empty tier by default
      this.addTier();
    }
  }

  get tiers(): FormArray {
    return this.addCoinTypeForm.get('tiers') as FormArray;
  }

  getTierMinimum(index: number): number {
    if (index === 0) {
      return 0;
    }
    const previousTier = this.tiers.at(index - 1);
    return previousTier?.get('breakPoint')?.value || 0;
  }

  onTierBreakPointChange() {
    // Trigger change detection to update tier range displays
    this.tiers.updateValueAndValidity();
  }

  addTier() {
    this.tiers.push(this.fb.group({
      breakPoint: [null, Validators.required],
      bonusAmount: [null, Validators.required],
      coinGain: [null, Validators.required]
    }));
  }

  removeTier(index: number) {
    // If editing, also remove the id from the dialogData.coinType.tiers
    if (this.isEdit && Array.isArray(this.dialogData?.coinType?.tiers)) {
      this.dialogData.coinType.tiers.splice(index, 1);
    }
    this.tiers.removeAt(index);
  }

  submit() {
    if (this.addCoinTypeForm.valid) {
      const { value, baseAmount, tiers } = this.addCoinTypeForm.value;
      let result: any = { value, baseAmount, tiers };
      if (this.dialogData && this.dialogData.coinType) {
        // Editing: keep the original id and activeAt, and preserve CoinTier ids after any deletions
        const originalTiers = this.dialogData.coinType.tiers || [];
        // Map ids by position: after any deletions, the order matches
        const tiersWithIds = tiers.map((tier: any, i: number) => {
          const orig = originalTiers[i];
          return orig && orig.id !== undefined ? { ...tier, id: orig.id } : tier;
        });
        result = {
          ...this.dialogData.coinType,
          value,
          baseAmount,
          tiers: tiersWithIds
        };
      } else {
        // Creating: set activeAt to now
        result = {
          value,
          baseAmount,
          activeAt: new Date(),
          tiers
        };
      }
      this.dialogRef.close(result);
    }
  }

  cancel() {
    this.dialogRef.close();
  }
}
